import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Eye, EyeOff, Save } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useSite } from "../../context/AppContext";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-amber-500 bg-white";

export default function AdminNavigation() {
  const { site, refreshSite } = useSite();
  const [nav, setNav] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (site?.navigation) setNav(JSON.parse(JSON.stringify(site.navigation)));
  }, [site]);

  if (!nav) return <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>;

  const move = (i, dir) => {
    const items = [...nav.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    [items[i], items[j]] = [items[j], items[i]];
    setNav({ ...nav, items });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", { navigation: nav });
      await refreshSite();
      toast.success("Navigation saved — live on your website");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-navigation">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Navigation & Buttons</h2>
          <p className="text-slate-500 text-sm">Rename tabs, reorder them, or hide pages from the menu.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-nav-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#0B1D3A] text-amber-300 text-sm font-semibold disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Navigation"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-[#0B1D3A] mb-3">Menu tabs</h3>
        <div className="space-y-2">
          {nav.items.map((item, i) => (
            <div key={item.id} className="flex items-center gap-2 border border-slate-100 rounded-lg p-2.5" data-testid={`nav-item-row-${item.id}`}>
              <input value={item.label} onChange={(e) => setNav({ ...nav, items: nav.items.map((n, idx) => (idx === i ? { ...n, label: e.target.value } : n)) })}
                className={inputCls} aria-label={`Label for ${item.path}`} data-testid={`nav-label-${item.id}`} />
              <span className="text-xs text-slate-400 hidden sm:block shrink-0 w-36 truncate">{item.path}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`nav-up-${item.id}`} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === nav.items.length - 1} aria-label="Move down" data-testid={`nav-down-${item.id}`} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
              <button
                onClick={() => setNav({ ...nav, items: nav.items.map((n, idx) => (idx === i ? { ...n, visible: !n.visible } : n)) })}
                aria-label={item.visible ? "Hide from menu" : "Show in menu"} data-testid={`nav-visibility-${item.id}`}
                className="p-2 rounded-lg hover:bg-slate-100">
                {item.visible ? <Eye className="w-4 h-4 text-slate-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">Hiding a tab removes it from the menu, but the page itself stays available at its address.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-[#0B1D3A] mb-3">Header "Plan Your Trip" button</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Button text</span>
            <input value={nav.headerCta.label} onChange={(e) => setNav({ ...nav, headerCta: { ...nav.headerCta, label: e.target.value } })} className={inputCls} data-testid="nav-cta-label" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Button link</span>
            <input value={nav.headerCta.url} onChange={(e) => setNav({ ...nav, headerCta: { ...nav.headerCta, url: e.target.value } })} className={inputCls} data-testid="nav-cta-url" />
          </label>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm text-slate-600">
          <input type="checkbox" checked={!!nav.headerCta.newTab} onChange={(e) => setNav({ ...nav, headerCta: { ...nav.headerCta, newTab: e.target.checked } })} className="accent-amber-600" data-testid="nav-cta-newtab" />
          Open in a new tab (recommended — it goes to your external travel website)
        </label>
      </div>
    </div>
  );
}
